// ─────────────────────────────────────────────────────────────────────────────
// OperatorAcknowledgement — §16.25 V-02 surface.
//
// Renders inline (within Edit Task) or full-screen. Allows the current user
// to acknowledge the current approved rev of a drawing for a task. POSTs to
// /api/task/:taskId/acknowledge-drawing with { drawing_id } — the backend
// derives the rev from the approved drawing and writes the row that satisfies
// the V-02 save-block (requireAck, enforced when ENFORCE_V02_ACK is on).
//
// 60 pt touch target on the primary action — iPad-first per §14.2.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Stack, Typography,
} from '@mui/material';
import { CheckCircle, FactCheck } from '@mui/icons-material';
import ApiCall from '../../utils/apicall';
import { showErrorToast, showSuccessToast } from '../../utils/Toast';

export default function OperatorAcknowledgement({
  taskId,
  drawingId,
  drawingRev,
  drawingName,
  existingAck,
  onAcknowledged,
  sx = {},
}) {
  const [submitting, setSubmitting] = useState(false);
  const acked = !!existingAck;

  async function handleAck() {
    if (acked || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await ApiCall.post(
        `/task/${taskId}/acknowledge-drawing`,
        { drawing_id: drawingId }
      );
      showSuccessToast('Drawing acknowledged.');
      if (onAcknowledged) onAcknowledged(data);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Acknowledgement failed.';
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'grey.200', borderRadius: 1, ...sx }}>
      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
        <FactCheck fontSize="small" sx={{ mr: 0.5 }} />
        Acknowledgement
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Drawing: <strong>{drawingName || `#${drawingId}`}</strong> (Rev {drawingRev})
      </Typography>
      {acked ? (
        <Alert icon={<CheckCircle />} severity="success" sx={{ mb: 1 }}>
          Acknowledged by {existingAck.acknowledged_by_name || existingAck.user_display_name
            || `user #${existingAck.acknowledged_by ?? existingAck.user_id ?? '—'}`}
          {(() => {
            const raw = existingAck.acknowledged_at || existingAck.created_at;
            const d = raw ? new Date(raw) : null;
            return d && !Number.isNaN(d.getTime())
              ? ` at ${d.toISOString().replace('T', ' ').slice(0, 16)} UTC`
              : '';
          })()}
        </Alert>
      ) : (
        <Stack spacing={1}>
          <Alert severity="info">
            Acknowledge that you have reviewed the current revision of this
            drawing before this task goes to inspection (AISC V-02 rev-lock).
          </Alert>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleAck}
            disabled={submitting}
            sx={{ minHeight: 60, fontSize: '1.05rem', fontWeight: 600 }}
            aria-label="Acknowledge Drawing"
          >
            {submitting ? 'Recording…' : 'Acknowledge Drawing'}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

OperatorAcknowledgement.propTypes = {
  taskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  drawingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  drawingRev: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  drawingName: PropTypes.string,
  existingAck: PropTypes.shape({
    // live backend shape (task_drawing_acknowledgements)
    acknowledged_by: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    acknowledged_by_name: PropTypes.string,
    acknowledged_at: PropTypes.string,
    // legacy shape (tolerated)
    user_id: PropTypes.number,
    user_display_name: PropTypes.string,
    created_at: PropTypes.string,
  }),
  onAcknowledged: PropTypes.func,
  sx: PropTypes.object,
};
